import { ApolloClient, from, HttpLink, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { RetryLink } from '@apollo/client/link/retry';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from 'apollo-link-error';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import { handleGraphQLErrors } from 'services/ErrorService/ErrorService';

// TODO: this needs to change after we replace parse completely
import { getSessionToken } from 'util/parseSelectors';
import { StatusCodes } from 'http-status-codes';

const makeAuthorizationHeader = async () => {
  const sessionToken = getSessionToken();
  const sessionTokenHeader = sessionToken ? { 'x-session-token': sessionToken } : null;
  const headers = { ...sessionTokenHeader };

  return headers;
};

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.REACT_APP_GRAPHQL_SUBSCRIPTION_URL,
    shouldRetry: (err) => true,
    retryAttempts: 5,
    on: {
      opened: () => {
        SessionAnalyticsService.event('GraphQL Subscriptions Connection Established');
      },
      closed: () => SessionAnalyticsService.event('**** graphql web socket connection closed ****'),
      connected: () => SessionAnalyticsService.event('**** GraphQL Subscriptions socket connected ****'),
      error: (err) => {
        SessionAnalyticsService.event('GraphQL Subscriptions Connection Error');
        handleGraphQLErrors(err);
      },
    },
    connectionParams: async () => {
      const sessionToken = getSessionToken();

      return {
        'x-session-token': sessionToken,
      };
    },
  })
);

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

const authMiddleware = setContext(() =>
  makeAuthorizationHeader().then((headers) => {
    return {
      headers: {
        ...headers,
      },
    };
  })
);

const errorLink = onError(handleGraphQLErrors);

// additional configuration ref: https://www.apollographql.com/docs/react/api/link/apollo-link-retry/
/*
  default configuration
  new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => !!error
  }
});
*/

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => {
      // Prevent retry attempts for unauthorized access.
      // For example, in the case of an expired session
      if (error?.statusCode === StatusCodes.UNAUTHORIZED) {
        return false;
      }

      return !!error;
    },
  },
});

// ⚠️ fetchPolicy: 'no-cache'
//
// Apollo Client’s caching can be surprising: by default every response is
// written to its in-memory store—even if you disable cache layers in the global
// config.  In our experience that leads to bloated memory footprints.
// We set fetchPolicy: 'no-cache' on every request to avoid that.

const defaultOptions = {
  watchQuery: { fetchPolicy: 'no-cache', nextFetchPolicy: 'no-cache', errorPolicy: 'ignore' },
  query: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
  mutate: { fetchPolicy: 'no-cache', errorPolicy: 'all' },
};

const cache = new InMemoryCache({
  resultCaching: false,
  resultCacheMaxSize: 0,
  cacheSizes: 0,
});

const apolloClient = new ApolloClient({
  link: from([errorLink, retryLink, authMiddleware, splitLink]),
  cache,
  queryDeduplication: false,
  connectToDevTools: process.env.NODE_ENV === 'development',
  defaultOptions,
});

export default apolloClient;
